
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {SecuritySettingsModifyResponse} from './SecuritySettingsModifyResponse';

/**
 * SecuritySettingsModifyRequest struct
 */
class SecuritySettingsModifyRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} securitySettingsId
   * @param {string=} securitySettingsName
   * @param {number=} newSortOrder
   * @param {string=} newName
   * @param {string=} newDescription
   * @param {boolean=} newIsFirewallEnabled
   * @param {boolean=} newIsAntifloodEnabled
   * @param {number=} newConnectionRateThreshold
   * @param {number=} newFailedLoginRateThreshold
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), securitySettingsId = null, securitySettingsName = null, newSortOrder = null, newName = null, newDescription = null, newIsFirewallEnabled = null, newIsAntifloodEnabled = null, newConnectionRateThreshold = null, newFailedLoginRateThreshold = null) {
    super()
    super.copy(parent)
    this.SecuritySettingsId = securitySettingsId
    this.SecuritySettingsName = securitySettingsName
    this.NewSortOrder = newSortOrder
    this.NewName = newName
    this.NewDescription = newDescription
    this.NewIsFirewallEnabled = newIsFirewallEnabled
    this.NewIsAntifloodEnabled = newIsAntifloodEnabled
    this.NewConnectionRateThreshold = newConnectionRateThreshold
    this.NewFailedLoginRateThreshold = newFailedLoginRateThreshold
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SecuritySettingsModifyRequest}
   * @param {!SecuritySettingsModifyRequest} other Other struct
   * @returns {!SecuritySettingsModifyRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.SecuritySettingsId != null) {
      this.SecuritySettingsId = new UUID(other.SecuritySettingsId)
    } else {
      this.SecuritySettingsId = null
    }
    if (other.SecuritySettingsName != null) {
      this.SecuritySettingsName = other.SecuritySettingsName
    } else {
      this.SecuritySettingsName = null
    }
    if (other.NewSortOrder != null) {
      this.NewSortOrder = other.NewSortOrder
    } else {
      this.NewSortOrder = null
    }
    if (other.NewName != null) {
      this.NewName = other.NewName
    } else {
      this.NewName = null
    }
    if (other.NewDescription != null) {
      this.NewDescription = other.NewDescription
    } else {
      this.NewDescription = null
    }
    if (other.NewIsFirewallEnabled != null) {
      this.NewIsFirewallEnabled = other.NewIsFirewallEnabled
    } else {
      this.NewIsFirewallEnabled = null
    }
    if (other.NewIsAntifloodEnabled != null) {
      this.NewIsAntifloodEnabled = other.NewIsAntifloodEnabled
    } else {
      this.NewIsAntifloodEnabled = null
    }
    if (other.NewConnectionRateThreshold != null) {
      this.NewConnectionRateThreshold = other.NewConnectionRateThreshold
    } else {
      this.NewConnectionRateThreshold = null
    }
    if (other.NewFailedLoginRateThreshold != null) {
      this.NewFailedLoginRateThreshold = other.NewFailedLoginRateThreshold
    } else {
      this.NewFailedLoginRateThreshold = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SecuritySettingsModifyRequest}
   * @returns {!SecuritySettingsModifyRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SecuritySettingsModifyRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SecuritySettingsModifyRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SecuritySettingsModifyRequest}
   * @param {!SecuritySettingsModifyRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SecuritySettingsModifyRequest)) {
      throw new TypeError('Instance of SecuritySettingsModifyRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SecuritySettingsModifyRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      SecuritySettingsId: ((this.SecuritySettingsId != null) ? this.SecuritySettingsId.toString() : null),
      SecuritySettingsName: ((this.SecuritySettingsName != null) ? this.SecuritySettingsName : null),
      NewSortOrder: ((this.NewSortOrder != null) ? this.NewSortOrder : null),
      NewName: ((this.NewName != null) ? this.NewName : null),
      NewDescription: ((this.NewDescription != null) ? this.NewDescription : null),
      NewIsFirewallEnabled: ((this.NewIsFirewallEnabled != null) ? this.NewIsFirewallEnabled : null),
      NewIsAntifloodEnabled: ((this.NewIsAntifloodEnabled != null) ? this.NewIsAntifloodEnabled : null),
      NewConnectionRateThreshold: ((this.NewConnectionRateThreshold != null) ? this.NewConnectionRateThreshold : null),
      NewFailedLoginRateThreshold: ((this.NewFailedLoginRateThreshold != null) ? this.NewFailedLoginRateThreshold : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SecuritySettingsModifyRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SecuritySettingsModifyRequest} other Object value
   * @returns {!SecuritySettingsModifyRequest} Created struct
   */
  static fromObject (other) {
    return new SecuritySettingsModifyRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SecuritySettingsModifyRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SecuritySettingsModifyRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SecuritySettingsModifyRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6538
  }
}

export { SecuritySettingsModifyRequest };

/**
 * Fast Binary Encoding SecuritySettingsModifyRequest field model
 */
class FieldModelSecuritySettingsModifyRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.SecuritySettingsId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.SecuritySettingsName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.SecuritySettingsId.fbeOffset + this.SecuritySettingsId.fbeSize), buffer, this.SecuritySettingsId.fbeOffset + this.SecuritySettingsId.fbeSize)
    this.NewSortOrder = new fbe.FieldModelOptional(new fbe.FieldModelUInt32(buffer, this.SecuritySettingsName.fbeOffset + this.SecuritySettingsName.fbeSize), buffer, this.SecuritySettingsName.fbeOffset + this.SecuritySettingsName.fbeSize)
    this.NewName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewSortOrder.fbeOffset + this.NewSortOrder.fbeSize), buffer, this.NewSortOrder.fbeOffset + this.NewSortOrder.fbeSize)
    this.NewDescription = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewName.fbeOffset + this.NewName.fbeSize), buffer, this.NewName.fbeOffset + this.NewName.fbeSize)
    this.NewIsFirewallEnabled = new fbe.FieldModelOptional(new fbe.FieldModelBool(buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize), buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize)
    this.NewIsAntifloodEnabled = new fbe.FieldModelOptional(new fbe.FieldModelBool(buffer, this.NewIsFirewallEnabled.fbeOffset + this.NewIsFirewallEnabled.fbeSize), buffer, this.NewIsFirewallEnabled.fbeOffset + this.NewIsFirewallEnabled.fbeSize)
    this.NewConnectionRateThreshold = new fbe.FieldModelOptional(new fbe.FieldModelUInt32(buffer, this.NewIsAntifloodEnabled.fbeOffset + this.NewIsAntifloodEnabled.fbeSize), buffer, this.NewIsAntifloodEnabled.fbeOffset + this.NewIsAntifloodEnabled.fbeSize)
    this.NewFailedLoginRateThreshold = new fbe.FieldModelOptional(new fbe.FieldModelUInt32(buffer, this.NewConnectionRateThreshold.fbeOffset + this.NewConnectionRateThreshold.fbeSize), buffer, this.NewConnectionRateThreshold.fbeOffset + this.NewConnectionRateThreshold.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.SecuritySettingsId.fbeSize + this.SecuritySettingsName.fbeSize + this.NewSortOrder.fbeSize + this.NewName.fbeSize + this.NewDescription.fbeSize + this.NewIsFirewallEnabled.fbeSize + this.NewIsAntifloodEnabled.fbeSize + this.NewConnectionRateThreshold.fbeSize + this.NewFailedLoginRateThreshold.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.SecuritySettingsId.fbeExtra + this.SecuritySettingsName.fbeExtra + this.NewSortOrder.fbeExtra + this.NewName.fbeExtra + this.NewDescription.fbeExtra + this.NewIsFirewallEnabled.fbeExtra + this.NewIsAntifloodEnabled.fbeExtra + this.NewConnectionRateThreshold.fbeExtra + this.NewFailedLoginRateThreshold.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSecuritySettingsModifyRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6538
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SecuritySettingsId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SecuritySettingsId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettingsId.fbeSize

    if ((fbeCurrentSize + this.SecuritySettingsName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SecuritySettingsName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettingsName.fbeSize

    if ((fbeCurrentSize + this.NewSortOrder.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewSortOrder.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewSortOrder.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewDescription.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewIsFirewallEnabled.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewIsFirewallEnabled.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewIsFirewallEnabled.fbeSize

    if ((fbeCurrentSize + this.NewIsAntifloodEnabled.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewIsAntifloodEnabled.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewIsAntifloodEnabled.fbeSize

    if ((fbeCurrentSize + this.NewConnectionRateThreshold.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewConnectionRateThreshold.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewConnectionRateThreshold.fbeSize

    if ((fbeCurrentSize + this.NewFailedLoginRateThreshold.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewFailedLoginRateThreshold.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewFailedLoginRateThreshold.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @param {!SecuritySettingsModifyRequest} fbeValue Default value, defaults is new SecuritySettingsModifyRequest()
   * @returns {!SecuritySettingsModifyRequest} SecuritySettingsModifyRequest value
   */
  get (fbeValue = new SecuritySettingsModifyRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @param {!SecuritySettingsModifyRequest} fbeValue SecuritySettingsModifyRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SecuritySettingsId.fbeSize) <= fbeStructSize) {
      fbeValue.SecuritySettingsId = this.SecuritySettingsId.get()
    } else {
      fbeValue.SecuritySettingsId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettingsId.fbeSize

    if ((fbeCurrentSize + this.SecuritySettingsName.fbeSize) <= fbeStructSize) {
      fbeValue.SecuritySettingsName = this.SecuritySettingsName.get()
    } else {
      fbeValue.SecuritySettingsName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettingsName.fbeSize

    if ((fbeCurrentSize + this.NewSortOrder.fbeSize) <= fbeStructSize) {
      fbeValue.NewSortOrder = this.NewSortOrder.get()
    } else {
      fbeValue.NewSortOrder = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewSortOrder.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) <= fbeStructSize) {
      fbeValue.NewName = this.NewName.get()
    } else {
      fbeValue.NewName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) <= fbeStructSize) {
      fbeValue.NewDescription = this.NewDescription.get()
    } else {
      fbeValue.NewDescription = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewIsFirewallEnabled.fbeSize) <= fbeStructSize) {
      fbeValue.NewIsFirewallEnabled = this.NewIsFirewallEnabled.get()
    } else {
      fbeValue.NewIsFirewallEnabled = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewIsFirewallEnabled.fbeSize

    if ((fbeCurrentSize + this.NewIsAntifloodEnabled.fbeSize) <= fbeStructSize) {
      fbeValue.NewIsAntifloodEnabled = this.NewIsAntifloodEnabled.get()
    } else {
      fbeValue.NewIsAntifloodEnabled = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewIsAntifloodEnabled.fbeSize

    if ((fbeCurrentSize + this.NewConnectionRateThreshold.fbeSize) <= fbeStructSize) {
      fbeValue.NewConnectionRateThreshold = this.NewConnectionRateThreshold.get()
    } else {
      fbeValue.NewConnectionRateThreshold = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewConnectionRateThreshold.fbeSize

    if ((fbeCurrentSize + this.NewFailedLoginRateThreshold.fbeSize) <= fbeStructSize) {
      fbeValue.NewFailedLoginRateThreshold = this.NewFailedLoginRateThreshold.get()
    } else {
      fbeValue.NewFailedLoginRateThreshold = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewFailedLoginRateThreshold.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @param {!SecuritySettingsModifyRequest} fbeValue SecuritySettingsModifyRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSecuritySettingsModifyRequest}
   * @param {SecuritySettingsModifyRequest} fbeValue SecuritySettingsModifyRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.SecuritySettingsId.set(fbeValue.SecuritySettingsId)
    this.SecuritySettingsName.set(fbeValue.SecuritySettingsName)
    this.NewSortOrder.set(fbeValue.NewSortOrder)
    this.NewName.set(fbeValue.NewName)
    this.NewDescription.set(fbeValue.NewDescription)
    this.NewIsFirewallEnabled.set(fbeValue.NewIsFirewallEnabled)
    this.NewIsAntifloodEnabled.set(fbeValue.NewIsAntifloodEnabled)
    this.NewConnectionRateThreshold.set(fbeValue.NewConnectionRateThreshold)
    this.NewFailedLoginRateThreshold.set(fbeValue.NewFailedLoginRateThreshold)
  }
}

export { FieldModelSecuritySettingsModifyRequest };

/**
 * Fast Binary Encoding SecuritySettingsModifyRequest model
 */
class SecuritySettingsModifyRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSecuritySettingsModifyRequest(this.buffer, 4)
  }

  /**
   * Get the SecuritySettingsModifyRequest model
   * @this {!SecuritySettingsModifyRequestModel}
   * @returns {!FieldModelSecuritySettingsModifyRequest} model SecuritySettingsModifyRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SecuritySettingsModifyRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SecuritySettingsModifyRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SecuritySettingsModifyRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SecuritySettingsModifyRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSecuritySettingsModifyRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SecuritySettingsModifyRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SecuritySettingsModifyRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SecuritySettingsModifyRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SecuritySettingsModifyRequestModel}
   * @param {!SecuritySettingsModifyRequest} value SecuritySettingsModifyRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SecuritySettingsModifyRequestModel}
   * @param {!SecuritySettingsModifyRequest} value SecuritySettingsModifyRequest value, defaults is new SecuritySettingsModifyRequest()
   * @return {!object} Deserialized SecuritySettingsModifyRequest value and its size
   */
  deserialize (value = new SecuritySettingsModifyRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SecuritySettingsModifyRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SecuritySettingsModifyRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SecuritySettingsModifyRequestModel}
   * @param {!number} prev Previous SecuritySettingsModifyRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SecuritySettingsModifyRequestModel };
SecuritySettingsModifyRequest.__has_response = true;
SecuritySettingsModifyRequest.__response_class = SecuritySettingsModifyResponse;
